import React, { useEffect, useState } from 'react';
import { CalendarDays, Save, Target } from 'lucide-react';
import { dailyCheckIns } from '../../utils/api';

const emptyReport = {
  date: '',
  conversations: '',
  appointmentsSet: '',
  note: '',
  conversationTarget: null,
};

export const formatReportingDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return '';
  const date = new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const TodayCard = ({ onSaved }) => {
  const [report, setReport] = useState(emptyReport);
  const [maxDate, setMaxDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadReport = async (date = null, preserveMaxDate = false) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await dailyCheckIns.agent.getToday(date);
      const data = response.data.data;
      setReport({
        date: data.date,
        conversations: data.conversations ?? '',
        appointmentsSet: data.appointmentsSet ?? '',
        note: data.note || '',
        conversationTarget: data.conversationTarget ?? null,
      });
      if (!preserveMaxDate) setMaxDate(data.date);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Could not load daily numbers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const updateField = (field, value) => {
    setReport((current) => ({ ...current, [field]: value }));
    setMessage('');
  };

  const changeDate = (date) => {
    if (!date) return;
    loadReport(date, true);
  };

  const saveReport = async () => {
    if (report.conversations === '' || report.appointmentsSet === '') {
      setError('Enter both numbers. Zero is a valid answer.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await dailyCheckIns.agent.saveToday({
        date: report.date,
        conversations: Number(report.conversations),
        appointmentsSet: Number(report.appointmentsSet),
        note: report.note,
      });
      const data = response.data.data;
      setReport((current) => ({
        ...current,
        conversations: data.conversations,
        appointmentsSet: data.appointmentsSet,
        note: data.note || '',
      }));
      setMessage('Numbers reported.');
      onSaved?.(data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Could not save daily numbers.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !report.date) {
    return (
      <section id="daily-numbers-card-loading" className="rounded-2xl border border-blue-100 bg-white p-6 shadow-sm">
        <p className="text-sm text-gray-500">Loading daily numbers…</p>
      </section>
    );
  }

  return (
    <section id="daily-numbers-card" className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
      <header id="daily-numbers-card-header" className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 text-white">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-100">Yesterday’s Numbers</p>
        <h3 className="mt-1 text-xl font-bold">Report two numbers</h3>
        <p className="mt-1 text-sm text-blue-100">
          {report.date ? `For ${formatReportingDate(report.date)}. ` : ''}Zero is a complete, valid report.
        </p>
      </header>

      <section id="daily-numbers-card-body" className="space-y-5 p-4 sm:p-5">
        <details id="daily-numbers-change-date" className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <summary className="cursor-pointer text-sm font-semibold text-gray-700">
            <CalendarDays className="mr-2 inline h-4 w-4" /> Need to report a different day?
          </summary>
          <label id="daily-numbers-date-field" htmlFor="daily-numbers-date" className="mt-3 block text-sm font-semibold text-gray-700">
            Reporting date
            <input
              id="daily-numbers-date"
              type="date"
              value={report.date}
              max={maxDate}
              onChange={(event) => changeDate(event.target.value)}
              className="mt-2 block min-h-12 w-full rounded-xl border border-gray-300 px-4 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:max-w-xs"
            />
          </label>
        </details>

        {report.conversationTarget && (
          <aside id="daily-conversation-target" className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-800">
            <Target className="mr-2 inline h-4 w-4" /> Your daily conversation goal is {report.conversationTarget}.
          </aside>
        )}

        <section id="daily-numbers-input-grid" className="grid gap-4 sm:grid-cols-2">
          <label id="daily-conversations-field" htmlFor="daily-conversations" className="block text-sm font-semibold text-gray-800">
            Conversations
            <span className="mt-1 block text-sm font-normal text-gray-500">How many two-way conversations did you have?</span>
            <input
              id="daily-conversations"
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              value={report.conversations}
              onChange={(event) => updateField('conversations', event.target.value)}
              className="mt-2 min-h-20 w-full rounded-2xl border-2 border-gray-200 px-5 text-center text-4xl font-bold text-gray-950 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
            />
          </label>
          <label id="daily-appointments-field" htmlFor="daily-appointments" className="block text-sm font-semibold text-gray-800">
            Appointments set
            <span className="mt-1 block text-sm font-normal text-gray-500">How many appointments did you set?</span>
            <input
              id="daily-appointments"
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              value={report.appointmentsSet}
              onChange={(event) => updateField('appointmentsSet', event.target.value)}
              className="mt-2 min-h-20 w-full rounded-2xl border-2 border-gray-200 px-5 text-center text-4xl font-bold text-gray-950 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
            />
          </label>
        </section>

        <p id="daily-numbers-zero-note" className="text-sm font-medium text-gray-600">None yesterday? Enter 0. Reporting 0 still counts.</p>

        {error && <p id="daily-numbers-card-error" className="text-sm font-medium text-rose-600">{error}</p>}
        {message && <p id="daily-numbers-card-success" className="text-sm font-medium text-emerald-700">{message}</p>}

        <button
          id="daily-numbers-save"
          type="button"
          onClick={saveReport}
          disabled={saving || loading}
          className="min-h-14 w-full rounded-xl bg-blue-600 px-5 py-3 text-lg font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Save className="mr-2 inline h-5 w-5" /> {saving ? 'Reporting…' : 'Report my numbers'}
        </button>
      </section>
    </section>
  );
};

export default TodayCard;
